/**
 * 48 点データ編集の undo / redo 履歴 (design/supply/48data.html の undoStack / redoStack 相当)。
 *
 * 1 エントリ = ユーザーの 1 操作。貼付・一括入力・範囲クリアのように複数セルを
 * 同時に書き換える操作も 1 エントリにまとめ、undo で丸ごと戻す。
 *
 * 連続入力 (1 セルへのキーストローク) は coalesceKey で 1 エントリに畳む:
 * 「5000」と打つと onChange が 5 / 50 / 500 / 5000 と 4 回発火するが、
 * undo 1 回で入力前の値へ戻したいため (= Excel の粒度)。
 */

/**
 * 1 セルへの書き込み指示。編集セッションの一括適用 (貼付 / 一括入力 / クリア) と
 * undo / redo の適用で共通に使う。
 */
export type CellEdit = {
  legId: string
  date: { year: number; month: number; day: number }
  koma: number
  value: number | null
  /**
   * 書き込み後の手動変更の印を明示する (非定型の取引の列のみ。AC-S4D-060)。
   * undo / redo (履歴の印) と Escape (focus 時点の印) が戻り先の印を渡すのに使う。省略時は「入力」で、非定型の列は印を付ける。
   */
  mark?: boolean
}

/** 1 セルの変更 (before → after) */
export type CellChange = {
  legId: string
  date: { year: number; month: number; day: number }
  koma: number
  before: number | null
  after: number | null
  /** 書き込み前後の手動変更の印 (非定型の取引の列のみ記録する。他は undefined) */
  markBefore?: boolean
  markAfter?: boolean
}

/** 1 操作分の履歴エントリ */
export type EditHistoryEntry = {
  changes: CellChange[]
  /**
   * 同一キーの連続適用を 1 エントリに畳むためのキー。
   * undefined のエントリには畳み込まない (= 貼付・一括入力は常に独立エントリ)。
   */
  coalesceKey: string | undefined
}

export type EditHistory = {
  /** 過去 (末尾が直近の操作) */
  past: EditHistoryEntry[]
  /** undo で戻した操作 (先頭が次に redo 対象) */
  future: EditHistoryEntry[]
}

export const EMPTY_EDIT_HISTORY: EditHistory = { past: [], future: [] }

/**
 * 履歴の上限。編集セッションは 1 画面 1 日分のため実運用では到達しないが、
 * 長時間の編集でメモリが際限なく伸びるのを防ぐ。
 */
const MAX_HISTORY_ENTRIES = 200

function cellKey(change: CellChange): string {
  const { date } = change
  return `${change.legId}::${date.year}-${date.month}-${date.day}::${change.koma}`
}

/** 値が変わっていない変更を落とす (no-op を履歴に積まない) */
function withoutNoop(changes: CellChange[]): CellChange[] {
  return changes.filter(
    (c) => c.before !== c.after || c.markBefore !== c.markAfter,
  )
}

/**
 * 操作を履歴に積む。
 *
 * coalesceKey が直近エントリと一致する場合は新規エントリを作らず、
 * 同一セルは before を保ったまま after だけを更新する (連続入力の畳み込み)。
 * 畳み込みの結果すべてのセルが元の値に戻った (= before === after) 場合は
 * エントリごと取り除く (Escape で入力前に戻した時に空エントリを残さない)。
 */
export function pushHistory(
  history: EditHistory,
  changes: CellChange[],
  coalesceKey: string | undefined,
): EditHistory {
  const last = history.past.at(-1)
  const canCoalesce =
    coalesceKey !== undefined &&
    last !== undefined &&
    last.coalesceKey === coalesceKey

  if (canCoalesce) {
    const merged = new Map<string, CellChange>()
    for (const change of last.changes) {
      merged.set(cellKey(change), change)
    }
    for (const change of changes) {
      const existing = merged.get(cellKey(change))
      merged.set(
        cellKey(change),
        existing === undefined
          ? change
          : {
              ...existing,
              after: change.after,
              markAfter: change.markAfter,
            },
      )
    }
    const mergedChanges = withoutNoop(Array.from(merged.values()))
    const past = history.past.slice(0, -1)
    return {
      past:
        mergedChanges.length === 0
          ? past
          : [...past, { changes: mergedChanges, coalesceKey }],
      future: [],
    }
  }

  const effective = withoutNoop(changes)
  if (effective.length === 0) {
    return history
  }
  const past = [...history.past, { changes: effective, coalesceKey }]
  return {
    past:
      past.length > MAX_HISTORY_ENTRIES
        ? past.slice(past.length - MAX_HISTORY_ENTRIES)
        : past,
    future: [],
  }
}

export function canUndo(history: EditHistory): boolean {
  return history.past.length > 0
}

export function canRedo(history: EditHistory): boolean {
  return history.future.length > 0
}

function toEdits(
  changes: CellChange[],
  pick: (change: CellChange) => { value: number | null; mark?: boolean },
): CellEdit[] {
  return changes.map((c) => ({
    legId: c.legId,
    date: c.date,
    koma: c.koma,
    ...pick(c),
  }))
}

/** undo 結果 (書き戻すセル + 新しい履歴)。戻せる操作がなければ null */
export function undoHistory(
  history: EditHistory,
): { history: EditHistory; edits: CellEdit[] } | null {
  const entry = history.past.at(-1)
  if (entry === undefined) return null
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [entry, ...history.future],
    },
    edits: toEdits(entry.changes, (c) => ({
      value: c.before,
      mark: c.markBefore,
    })),
  }
}

/** redo 結果 (書き戻すセル + 新しい履歴)。やり直せる操作がなければ null */
export function redoHistory(
  history: EditHistory,
): { history: EditHistory; edits: CellEdit[] } | null {
  const entry = history.future[0]
  if (entry === undefined) return null
  return {
    history: {
      past: [...history.past, entry],
      future: history.future.slice(1),
    },
    edits: toEdits(entry.changes, (c) => ({
      value: c.after,
      mark: c.markAfter,
    })),
  }
}
